// WeeklyLineupManager.js — Enhanced with Captain Selection Feature and Trip Play
import React, { useState, useEffect } from 'react';
import { supabase } from '../supabase/supabase';
import { weeklyLineupUtils } from '../utils/weeklyLineupUtils';
import { anySuperDog } from '../utils/superDog';
import SuperDogLegend from './SuperDogLegend';
import HelpMark from './HelpMark';
import ShareButton from './ShareButton';
import TourAnchor from '../tours/TourAnchor';
import { useFreezeEvents } from '../hooks/useFreezeEvents';
import { buildFreezeSnapshot } from '../utils/freezeDetail';
import { buildLineupShareText, weekRankOf } from '../utils/lineupShare';
import { ChevronLeft, ChevronRight, Crown, Zap, Snowflake, X } from 'lucide-react';
import { useLeague } from '../context/LeagueContext';
import { canBeCaptain, canUseTripPlay, canMoveTeam, buildGamesBySlug, anyGameSoon, POLL_MS } from '../utils/gameStatus';
import { FIRST_TRIP_PLAY_WEEK, LAST_TRIP_PLAY_WEEK, SEASON_YEAR } from '../utils/season';
import TeamSlot from './lineup/TeamSlot';
import SwapOptionRow from './lineup/SwapOptionRow';
import ConfirmCutModal from './lineup/ConfirmCutModal';
import CenteredModal from './sheets/CenteredModal';
import BottomSheet from './sheets/BottomSheet';
import { ModalPortal, useLockBodyScroll } from './sheets/ModalPortal';
import { hapticSuccess, hapticImpact } from '../capacitor/haptics';
import { invalidateLeague } from '../utils/queryCache';
import { titleCase } from '../utils/titleCase';

/* ModalPortal/useLockBodyScroll now live in ./sheets/ModalPortal — re-exported
   here so existing imports keep working. */
export { ModalPortal, useLockBodyScroll };

/* ---------- WeeklyLineupManager ---------- */
// The three places a team can sit. Each group lists the slots a swap can
// target; the captain is a starters slot, so it is pulled out by index rather
// than stored apart.
// Fixed shape of a lineup: five starters (one of them the captain) and two
// bench slots.
const STARTER_SLOTS = 5;
const BENCH_SLOTS = 2;

const padSlots = (slots, size) =>
  slots.length >= size ? slots : [...slots, ...Array(size - slots.length).fill(null)];

// "the captain", "a starter", "a bench team": what the tapped pill was.
const swapLabel = ({ section, index }, captainIndex) =>
  section === 'starters' && index === captainIndex ? 'captain' : section === 'starters' ? 'starter' : 'bench team';

const SWAP_GROUPS = [
  {
    key: 'captain',
    label: 'Captain',
    slots: ({ captainIndex }) => (captainIndex >= 0 ? [{ section: 'starters', index: captainIndex }] : []),
  },
  {
    key: 'starters',
    label: 'Starters',
    slots: ({ starters, captainIndex }) =>
      starters.map((_, index) => ({ section: 'starters', index })).filter((s) => s.index !== captainIndex),
  },
  {
    key: 'bench',
    label: 'Bench',
    slots: ({ bench }) => bench.map((_, index) => ({ section: 'bench', index })),
  },
];

const WeeklyLineupManager = ({
  // Owned by MyLineup, which needs the same state for its chips. Mounting the
  // hook here as well gave the page two copies of one manager's freezes and
  // 5x Play, and only the copy that performed a write ever heard about it.
  lineupState,
  leagueId,
  memberId,
  allTeams,
  currentWeek,
  onTeamClick,
  TeamLogo,
  onOpenTeam,
  userDisplayName,
  // The public demo league: shown, never edited, freezes included.
  readOnly = false
}) => {
  const {
    selectedWeek,
    setSelectedWeek,
    weeklyLineups,
    loading,
    hasTripPlay,
    tripPlayUsedWeek,
    freezesRemaining,
    saveLineup,
    saveFreezePlay,
    refresh: refreshLineup,
  } = lineupState;

  // The season total on the page header reads league_members.points out of
  // LeagueContext, which loads once. That context subscribes to realtime for
  // the table, but the supabase_realtime publication is empty, so nothing ever
  // arrives and the number sat still while every other figure on the page
  // moved. Pulled on the same tick as the rest.
  const { refreshLeague, members } = useLeague();

  // Schedule/score source for TeamSlot: the `games` table, not
  // team_season_stats.next_opponent (see buildGamesBySlug's comment in
  // gameStatus.js for why). One query per selected week, shared by every
  // row instead of each TeamSlot querying for itself.
  const [gamesBySlug, setGamesBySlug] = useState({});
  // Fantasy points per team slug for the selected week, read fresh rather than
  // from the AuthContext teams map.
  //
  // That map is loaded once at boot and never refetched, so it holds whatever
  // points existed when the app opened. Now that espn.js scores a game while
  // it runs, a stale map is the difference between a live row and a row stuck
  // on the value it had an hour ago.
  const [weeklyPointsBySlug, setWeeklyPointsBySlug] = useState({});
  // Whether this week is worth polling: something is running, or a kickoff is
  // close enough that one is about to be.
  const [isGameday, setIsGameday] = useState(false);
  // Which manager holds each team this week, keyed by slug, so a row can name
  // the manager on the other side of its game. Read from every member's
  // weekly_lineups row rather than draft_picks, which stops being the roster
  // once free agency moves a team.
  //
  // Loaded per week and not on the gameday poll: rosters only change when
  // waivers resolve on a Tuesday, and repeating this every minute would be
  // egress spent on an answer that cannot have changed.
  const [ownerMemberBySlug, setOwnerMemberBySlug] = useState({});
  // Every manager's total for the week, from the same rows, for the rank a
  // shared lineup quotes. It has to be in hand before the tap: Safari only
  // opens the share sheet inside the tap itself, and a fetch in between
  // loses it. So like the owners above it is as old as the page load, which
  // on a live Saturday can put the other managers a few minutes behind.
  const [weekTotals, setWeekTotals] = useState([]);
  // Whether this week's games have arrived. Without it the rows render for a
  // frame against an empty gamesBySlug, and a team with no game row reads as
  // a bye, so every team flashed "Bye week" before the real fixtures landed.
  const [gamesLoaded, setGamesLoaded] = useState(false);
  // The snapshot each freeze this week was written with, for the panel under
  // a frozen row. Refetched when the count of frozen teams changes, which is
  // how this manager's own freeze a moment ago shows its detail.
  const frozenCount = (weeklyLineups[`week${selectedWeek}`]?.frozenTeams || []).length;
  const freezeEvents = useFreezeEvents({
    leagueId, week: selectedWeek, enabled: frozenCount > 0, refreshKey: frozenCount,
  });

  useEffect(() => {
    if (!leagueId) return undefined;
    let cancelled = false;

    supabase
      .from('weekly_lineups')
      .select('member_id, starters, bench, points')
      .eq('league_id', leagueId)
      .eq('week', selectedWeek)
      .then(({ data }) => {
        if (cancelled) return;
        const owners = {};
        for (const row of data || []) {
          for (const slug of [...(row.starters || []), ...(row.bench || [])]) {
            if (slug) owners[slug] = row.member_id;
          }
        }
        setOwnerMemberBySlug(owners);
        setWeekTotals((data || []).map((row) => ({ memberId: row.member_id, points: row.points })));
      });

    return () => { cancelled = true; };
  }, [leagueId, selectedWeek]);

  useEffect(() => {
    let cancelled = false;
    // A new week starts unknown again, so switching weeks does not show the
    // previous week's fixtures or a screenful of byes.
    setGamesLoaded(false);

    async function loadWeek() {
      const [{ data: games }, { data: stats }] = await Promise.all([
        supabase
          .from('games')
          .select('home_team, away_team, home_score, away_score, home_spread, period, clock, game_status, game_complete, date')
          .eq('year', SEASON_YEAR)
          .eq('week', selectedWeek),
        supabase
          .from('team_season_stats')
          .select('team_id, weekly_points')
          .eq('season_year', SEASON_YEAR),
      ]);

      if (cancelled) return;

      setGamesBySlug(buildGamesBySlug(games));

      const points = {};
      for (const row of stats || []) {
        points[row.team_id] = row.weekly_points || {};
      }
      setWeeklyPointsBySlug(points);

      // Drives the poll below. Anything running, or anything kicking off
      // within the hour: a page opened at ten to seven has to start polling
      // on its own, since nothing will remount it when the game starts.
      setIsGameday(anyGameSoon(games));
      // Set even when the query came back empty: a week with no rows is an
      // answer, and holding the spinner forever would be worse than showing
      // the lineup without fixtures.
      setGamesLoaded(true);
    }

    loadWeek();

    const tick = () => {
      if (document.visibilityState !== 'visible') return;
      loadWeek();
      // The row scores come from loadWeek; the week total on the header is
      // written by the scorer onto weekly_lineups, and the season total lives
      // on league_members, so each needs its own pull.
      refreshLineup?.();
      refreshLeague?.();
    };

    // Coming back to the app always refetches, whatever the week. Phones
    // suspend a backgrounded page, so this is what makes reopening the app
    // mid-afternoon show the afternoon's scores rather than the morning's.
    document.addEventListener('visibilitychange', tick);
    window.addEventListener('focus', tick);

    // The interval is gameday only. espn.js writes on a 2 minute cron, so a
    // 60 second poll sits comfortably inside its cadence without doubling up,
    // and an idle week costs one fetch rather than one a minute all season.
    const id = isGameday ? setInterval(tick, POLL_MS) : null;

    return () => {
      cancelled = true;
      if (id) clearInterval(id);
      document.removeEventListener('visibilitychange', tick);
      window.removeEventListener('focus', tick);
    };
    // refreshLineup is rebuilt on every render of the hook, so listing it here
    // would tear down and re-arm the poll on each pass.
  }, [selectedWeek, isGameday]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) {
    return (
      <div className="bg-surface-card sm:rounded-2xl p-6 border-y sm:border border-hairline shadow-sm">
        <div className="text-center">
          <div className="w-8 h-8 border-4 rounded-full animate-spin mx-auto mb-3" style={{ borderColor: 'var(--color-border)', borderTopColor: 'var(--color-brand)' }} />
          <p style={{ color: 'var(--color-text-muted)' }}>Loading weekly lineups...</p>
        </div>
      </div>
    );
  }

  const weekPoints = weeklyLineups[`week${selectedWeek}`]?.points ?? null;

  // This manager's freezes for the week on screen, by team.
  const freezeDetailBySlug = {};
  freezeEvents.forEach((detail, key) => {
    const [owner, slug] = key.split(':');
    if (owner === String(memberId)) freezeDetailBySlug[slug] = detail;
  });

  // The week on screen as share text. This manager's own total is the fresh
  // one off the page, swapped into the totals loaded with it, so the rank is
  // at least right about the lineup being shared.
  const getShareText = () => {
    const lineup = weeklyLineups[`week${selectedWeek}`] || {};
    const me = (members || []).find((m) => m.id === memberId);
    const totals = weekTotals.map((t) => (t.memberId === memberId ? { ...t, points: weekPoints } : t));

    return buildLineupShareText({
      teamName: me?.team_name || 'Unnamed team',
      managerName: me?.first_name,
      week: selectedWeek,
      points: weekPoints,
      starters: (lineup.starters || []).filter(Boolean),
      captain: lineup.captain,
      tripPlayTeam: lineup.tripPlayTeam,
      frozenTeams: lineup.frozenTeams,
      teamPoints: lineup.teamPoints,
      allTeams,
      gamesBySlug,
      weeklyPointsBySlug,
      weekRank: weekRankOf(memberId, totals),
      managerCount: totals.length,
    });
  };

  // Slug to the manager shown in brackets after an opponent, with the member
  // id the Scoreboard deep link needs. First name, since a team name is often
  // long enough to clip the line it sits on. members comes from LeagueContext
  // already masked for blocks. The viewer's own teams say "You", which is what
  // happens when a manager starts both sides of a game.
  const ownerBySlug = {};
  for (const [slug, ownerId] of Object.entries(ownerMemberBySlug)) {
    if (ownerId === memberId) {
      ownerBySlug[slug] = { name: 'You', memberId: ownerId };
      continue;
    }
    const owner = (members || []).find((m) => m.id === ownerId);
    const name = owner?.first_name || owner?.team_name;
    if (name) ownerBySlug[slug] = { name, memberId: ownerId };
  }

  return (
    <div className="bg-surface-card sm:rounded-2xl border-y sm:border border-hairline shadow-sm overflow-hidden">
      {/* Week switcher: deliberately minimal, one line, no permanent chrome.
          Forward stops at the current week. A future week has no lineup to
          show and cannot be edited, so it only offered an empty page.
          The week's total sits at the right end, absolutely positioned so the
          switcher itself stays optically centred whether or not there is a
          score to show.

          This is the number the page is about and it was the one number not on
          it: the header carries the season total, every row carries its own
          score, and nothing added the week up. Adding the column up by eye
          does not work either, since the captain doubles and the 5x Play
          multiplies. */}
      <div className="relative flex items-center justify-center gap-1 py-2.5" style={{ borderBottom: '1px solid var(--color-border)' }}>
        {/* Opposite the week's total, and on this row rather than the page
            header because this row is what says which week is on screen: the
            share is of that week's lineup, not always the current one. */}
        {gamesLoaded && (
          <div className="absolute" style={{ left: 4 }}>
            <ShareButton getText={getShareText} label={`Share my week ${selectedWeek} lineup`} />
          </div>
        )}
        <button
          onClick={() => setSelectedWeek(Math.max(1, selectedWeek - 1))}
          disabled={selectedWeek <= 1}
          aria-label="Previous week"
          style={{ background: 'none', border: 'none', padding: 8, color: selectedWeek <= 1 ? 'var(--color-text-faint)' : 'var(--color-brand)' }}
        >
          <ChevronLeft size={18} />
        </button>
        <span className="text-base font-bold" style={{ color: 'var(--color-text-strong)', minWidth: 72, textAlign: 'center' }}>
          Week {selectedWeek}
        </span>
        <button
          onClick={() => setSelectedWeek(Math.min(currentWeek, selectedWeek + 1))}
          disabled={selectedWeek >= currentWeek}
          aria-label="Next week"
          style={{ background: 'none', border: 'none', padding: 8, color: selectedWeek >= currentWeek ? 'var(--color-text-faint)' : 'var(--color-brand)' }}
        >
          <ChevronRight size={18} />
        </button>

        {/* Null while a week is unscored, which is not the same as zero: an
            empty lineup really does score zero. */}
        {weekPoints != null && (
          <div
            className="absolute flex items-baseline gap-1"
            style={{ right: 12 }}
          >
            <span className="text-base font-bold" style={{ color: 'var(--color-text-strong)' }}>
              {weekPoints}
            </span>
            <span className="text-[10px] font-semibold" style={{ color: 'var(--color-text-muted)' }}>PTS</span>
          </div>
        )}
      </div>

      {/* Lineup Content */}
      <div className="p-3 sm:p-5">
        <WeeklyLineupContent
          week={selectedWeek}
          lineup={weeklyLineups[`week${selectedWeek}`]}
          allTeams={allTeams}
          isEditable={!readOnly && selectedWeek === currentWeek}
          readOnly={readOnly}
          onSave={(starters, bench, captain, tripPlayTeam) => saveLineup(selectedWeek, starters, bench, captain, tripPlayTeam)}
          onFreezePlay={async (normalizedTeamName, frozenPoints, snapshot) => {
            await saveFreezePlay(selectedWeek, normalizedTeamName, frozenPoints, snapshot);
            hapticSuccess();
          }}
          onTeamClick={onTeamClick}
          TeamLogo={TeamLogo}
          onOpenTeam={onOpenTeam}
          leagueId={leagueId}
          memberId={memberId}
          userDisplayName={userDisplayName}
          currentWeek={currentWeek}
          hasTripPlay={hasTripPlay}
          tripPlayUsedWeek={tripPlayUsedWeek}
          freezesRemaining={freezesRemaining}
          gamesBySlug={gamesBySlug}
          gamesLoaded={gamesLoaded}
          weeklyPointsBySlug={weeklyPointsBySlug}
          ownerBySlug={ownerBySlug}
          freezeDetailBySlug={freezeDetailBySlug}
        />
      </div>
    </div>
  );
};

const WeeklyLineupContent = ({
  week,
  lineup,
  allTeams,
  isEditable,
  readOnly = false,
  onSave,
  onFreezePlay,
  onTeamClick,
  TeamLogo,
  onOpenTeam,
  leagueId,
  memberId,
  userDisplayName,
  currentWeek,
  hasTripPlay,
  tripPlayUsedWeek,
  freezesRemaining,
  gamesBySlug,
  gamesLoaded,
  ownerBySlug,
  weeklyPointsBySlug,
  freezeDetailBySlug,
}) => {
  const [starters, setStarters] = useState(Array(STARTER_SLOTS).fill(null));
  const [bench, setBench] = useState(Array(BENCH_SLOTS).fill(null));
  const [captain, setCaptain] = useState(null);
  const [tripPlayTeam, setTripPlayTeam] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [showConfirmCut, setShowConfirmCut] = useState(null);
  const [showFreezeConfirm, setShowFreezeConfirm] = useState(null); // { team, points }
  const [isSaving, setIsSaving] = useState(false);
  // One inline message for everything this component needs to tell a manager:
  // a refused write, a rule that blocks the action they just tried.
  //
  // These were window.alert calls, twelve of them. A native dialog prints the
  // page origin above the message ("...ngrok-free.app says"), blocks the whole
  // page, and looks nothing like the rest of the app. It is also the one piece
  // of UI that cannot be styled or dismissed by tapping away.
  const [notice, setNotice] = useState(null);
  const canEdit = isEditable && (week === currentWeek) && (lineup?.isEditable !== false);

  useEffect(() => {
    if (lineup) {
      // Padded to the fixed 5 and 2. A saved lineup can be short: compile
      // writes only the slots it filled, and an older row can hold an empty
      // bench array. The missing slots then rendered as no rows at all, so a
      // bench with nothing in it offered nowhere to bench anyone to.
      const resolvedStarters = padSlots((lineup.starters || []).map(n => n ? findTeamByNormalizedName(n) : null), STARTER_SLOTS);
      const resolvedBench   = padSlots((lineup.bench || []).map(n => n ? findTeamByNormalizedName(n) : null), BENCH_SLOTS);
      setStarters(resolvedStarters);
      setBench(resolvedBench);
      setCaptain(lineup.captain || null);
      setTripPlayTeam(lineup.tripPlayTeam || null);
    } else {
      setStarters(Array(STARTER_SLOTS).fill(null));
      setBench(Array(BENCH_SLOTS).fill(null));
      setCaptain(null);
      setTripPlayTeam(null);
    }
    setHasChanges(false);
    // findTeamByNormalizedName is redefined every render; it only reads
    // allTeams, which is already a dependency, so it's safe to omit here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lineup, allTeams]);

  const findTeamByNormalizedName = (normalizedName) =>
    Object.values(allTeams).find(team => weeklyLineupUtils.normalizeTeamName(team) === normalizedName);

  // Captaincy is no longer a toggle. It belongs to the captain slot, and
  // handleSwap moves it, so the old handleCaptainSelect is gone: it existed
  // only for the crown icon the pills replaced.

  const handleTripPlaySelect = (teamName) => {
    // Find the actual team object from starters or bench
    const teamObject = [...starters, ...bench]
      .filter(team => team !== null)
      .find(team => (team.school || team.name) === teamName);

    if (!teamObject) {
      setNotice("Team not found in lineup");
      return;
    }

    const normalizedTeamName = weeklyLineupUtils.normalizeTeamName(teamObject);
    const isCurrentlyTripPlay = tripPlayTeam === normalizedTeamName;

    // If we're trying to REMOVE trip play (team currently has it)
    if (isCurrentlyTripPlay) {
      // Allow removal - this will be handled in saveLineup
      const newTripPlayTeam = null;
      setTripPlayTeam(newTripPlayTeam);
      setHasChanges(true);
      return;
    }

    // If we're trying to ADD trip play but it's not available
    if (!hasTripPlay) {
      setNotice("5x Play has already been used this season");
      return;
    }

    // The 5x Play opens in week 2 and closes at the end of the regular season
    if (week < FIRST_TRIP_PLAY_WEEK) {
      setNotice(`The 5x Play opens in Week ${FIRST_TRIP_PLAY_WEEK}.`);
      return;
    }
    if (week > LAST_TRIP_PLAY_WEEK) {
      setNotice(`The 5x Play closed after Week ${LAST_TRIP_PLAY_WEEK}. It cannot be used in the playoffs.`);
      return;
    }

    // Check if team can use trip play (must be in starters)
    if (!canUseTripPlay(teamObject, starters, bench)) {
      setNotice("The 5x Play must be selected from your starters");
      return;
    }

    // The 5x Play goes on the captain and nowhere else: it triples where the
    // captain doubles, and the two combine into the flat 5x branch in
    // backend/scoring.js, not a stacked 6x.
    if (!captain) {
      setNotice("Pick your captain first. The 5x Play goes on your captain.");
      return;
    }
    if (captain !== normalizedTeamName) {
      setNotice("The 5x Play can only go on your captain.");
      return;
    }

    // Add trip play to this team
    setTripPlayTeam(normalizedTeamName);
    setHasChanges(true);
  };

  // A message about the week you were looking at must not follow you to the
  // next one.
  useEffect(() => { setNotice(null); }, [week]);

  const saveLineupChanges = async (newStarters, newBench, newCaptain = captain, newTripPlayTeam = tripPlayTeam) => {
    try {
      setIsSaving(true);
      setNotice(null);

      const validatedCaptain = newCaptain && canBeCaptain(newCaptain, newStarters, newBench) ? newCaptain : null;
      const validatedTripPlayTeam = newTripPlayTeam && canUseTripPlay(newTripPlayTeam, newStarters, newBench) ? newTripPlayTeam : null;

      await onSave(newStarters, newBench, validatedCaptain, validatedTripPlayTeam);
      setStarters(newStarters);
      setBench(newBench);
      setCaptain(validatedCaptain);
      setTripPlayTeam(validatedTripPlayTeam);
      setHasChanges(false);
    } catch (e) {
      console.error("Error saving lineup:", e);
      // Migration 049 raises this from a trigger on weekly_lineups. Matching
      // on the message is unpleasant, but the alternative is letting a refused
      // write look like a silent failure.
      if (String(e?.message || "").includes("Lineup locked")) {
        setNotice(
          "That team has already kicked off, so your lineup is locked for it. Your other teams are still open."
        );
      }
    } finally {
      setIsSaving(false);
    }
  };

  // Moves happen through the slot pills and the swap sheet (handleSwap).
  // Drag and drop is gone: it expressed one swap through a gesture nobody
  // could see, needed a long-press delay so a tap still worked, and fought
  // the swipe-to-cut handler on the same row.

  const handleTeamCut = async (team, section, index) => {
    const teamName = team.school || team.name;
    const moveValidation = canMoveTeam(gamesBySlug[weeklyLineupUtils.normalizeTeamName(team)], teamName);

    if (!moveValidation.canMove) {
      setNotice(`Cannot cut ${teamName}: ${moveValidation.message}`);
      return;
    }

    setShowConfirmCut({ team, section, index });
  };

  const confirmCut = async () => {
    if (!showConfirmCut || isSaving) return;

    const { team, section, index } = showConfirmCut;
    const newStarters = [...starters];
    const newBench = [...bench];
    const normalizedTeamName = weeklyLineupUtils.normalizeTeamName(team);

    if (section === 'starters') newStarters[index] = null; else newBench[index] = null;

    const newCaptain = captain === normalizedTeamName ? null : captain;
    const newTripPlayTeam = tripPlayTeam === normalizedTeamName ? null : tripPlayTeam;

    try {
      setIsSaving(true);
      await saveLineupChanges(newStarters, newBench, newCaptain, newTripPlayTeam);
      hapticImpact('medium');

      // Recording the cut is separate from making it. These columns were
      // team_name/move_type/dropped/picked_up, none of which exist on
      // move_history, so every insert here failed and was swallowed by the
      // catch below: cuts never reached the league activity feed. The real
      // columns are team_added/team_dropped, holding slugs, which is what the
      // waiver and instant-add RPCs write.
      //
      // A failure to log must not read as a failed cut, so it has its own
      // catch. The lineup change is already saved by this point.
      try {
        const { error: logError } = await supabase.from('move_history').insert({
          league_id: leagueId,
          member_id: memberId,
          week: Number.isFinite(Number(currentWeek)) ? Number(currentWeek) : null,
          team_added: null,
          team_dropped: normalizedTeamName,
        });
        if (logError) throw logError;
        invalidateLeague(leagueId);
      } catch (logErr) {
        console.error("Cut saved, but recording it in league activity failed:", logErr);
      }
    } catch (e) {
      console.error("Error cutting team:", e);
    } finally {
      setIsSaving(false);
      setShowConfirmCut(null);
    }
  };

  const handleFreezeRequest = (team, points) => setShowFreezeConfirm({ team, points });

  const confirmFreeze = async () => {
    if (!showFreezeConfirm || isSaving) return;
    const { team, points } = showFreezeConfirm;
    const slug = weeklyLineupUtils.normalizeTeamName(team);
    // The game as it stands right now goes in with the freeze: nothing else
    // records the scoreline it was taken at, and it cannot be worked out
    // afterwards.
    const snapshot = buildFreezeSnapshot({
      gameInfo: gamesBySlug?.[slug],
      slug,
      points,
      isCaptain: captain === slug,
      isTripPlay: tripPlayTeam === slug,
    });
    await onFreezePlay(slug, points, snapshot);
    setShowFreezeConfirm(null);
  };

  const handleSave = async () => {
    if (hasChanges && canEdit && !isSaving) await saveLineupChanges(starters, bench, captain, tripPlayTeam);
  };

  // The captain gets its own section, so the starters list renders around it.
  // Its index is kept, not reassigned: drag still writes to the same
  // `starters` slot it always did, and the captain is still one of the five.
  // Swap sheet: which slot was tapped, and which rows to flash after a move.
  const [swapFrom, setSwapFrom] = useState(null);      // { section, index }
  const [justMovedSlots, setJustMovedSlots] = useState([]); // ["starters-2", ...]

  const captainIndex = captain
    ? starters.findIndex((t) => t && weeklyLineupUtils.normalizeTeamName(t) === captain)
    : -1;

  // One move for every case the pills offer: captain to bench, bench to
  // starter, starter to captain. The two teams change places, and whichever
  // ends up in the captain's slot is the captain, so making a bench team
  // captain is a single swap rather than a promote and then a crown tap.
  //
  // The 5x Play follows the captain, because migration 032 refuses a lineup
  // where it does not.
  const handleSwap = async (from, to) => {
    setSwapFrom(null);
    if (!canEdit || isSaving) return;
    if (from.section === to.section && from.index === to.index) return;

    const newStarters = [...starters];
    const newBench = [...bench];
    const pick = (slot) => (slot.section === 'starters' ? newStarters : newBench)[slot.index];
    const put = (slot, value) => {
      if (slot.section === 'starters') newStarters[slot.index] = value;
      else newBench[slot.index] = value;
    };

    const fromTeam = pick(from);
    const toTeam = pick(to);

    for (const team of [fromTeam, toTeam]) {
      if (!team) continue;
      const name = team.school || team.name;
      const validation = canMoveTeam(gamesBySlug[weeklyLineupUtils.normalizeTeamName(team)], name);
      if (!validation.canMove) {
        setNotice(`Cannot move ${name}: ${validation.message}`);
        return;
      }
    }

    put(to, fromTeam);
    put(from, toTeam || null);

    // Captaincy belongs to the slot, not the team. Whoever holds the captain
    // slot after the swap is captain; if the swap empties it, there is no
    // captain and the 6:30 job will set one.
    let newCaptain = captain;
    if (captainIndex >= 0 && (from.section === 'starters' || to.section === 'starters')) {
      const captainOccupant = newStarters[captainIndex];
      if (from.index === captainIndex && from.section === 'starters') {
        newCaptain = captainOccupant ? weeklyLineupUtils.normalizeTeamName(captainOccupant) : null;
      } else if (to.index === captainIndex && to.section === 'starters') {
        newCaptain = weeklyLineupUtils.normalizeTeamName(fromTeam);
      }
    }
    const newTripPlay = tripPlayTeam ? newCaptain : tripPlayTeam;

    setJustMovedSlots([`${from.section}-${from.index}`, `${to.section}-${to.index}`]);
    await saveLineupChanges(newStarters, newBench, newCaptain, newTripPlay);
    hapticImpact('medium');
  };

  // No captain yet, so there is no captain slot to swap with. Picking one
  // from the sheet makes that team captain where it stands; a bench team is
  // moved into a starters slot first, preferring an empty one so nobody is
  // benched by a choice that did not mention them.
  const handleCaptainPick = async (slot) => {
    setSwapFrom(null);
    if (!canEdit || isSaving) return;

    const newStarters = [...starters];
    const newBench = [...bench];
    const team = slot.section === 'starters' ? newStarters[slot.index] : newBench[slot.index];
    if (!team) return;

    const name = team.school || team.name;
    const validation = canMoveTeam(gamesBySlug[weeklyLineupUtils.normalizeTeamName(team)], name);
    if (!validation.canMove) {
      setNotice(`Cannot make ${name} captain: ${validation.message}`);
      return;
    }

    const moved = [`${slot.section}-${slot.index}`];
    if (slot.section === 'bench') {
      let target = newStarters.findIndex((t) => !t);
      if (target < 0) {
        setNotice('Your starters are full. Swap a starter to the bench first.');
        return;
      }
      newStarters[target] = team;
      newBench[slot.index] = null;
      moved.push(`starters-${target}`);
    }

    setJustMovedSlots(moved);
    await saveLineupChanges(newStarters, newBench, weeklyLineupUtils.normalizeTeamName(team), tripPlayTeam);
    hapticImpact('medium');
  };

  // The flash is a confirmation, not a state: it says which two rows just
  // changed and then gets out of the way.
  useEffect(() => {
    if (justMovedSlots.length === 0) return undefined;
    const t = setTimeout(() => setJustMovedSlots([]), 2500);
    return () => clearTimeout(t);
  }, [justMovedSlots]);

  // Only when something on this lineup carries the rule. A legend for a mark
  // that is not on screen is noise, and the rule only applies to a handful of
  // games in any given week.
  // normalizeTeamName here takes the team OBJECT and returns null for anything
  // without a .school, so passing a string silently yields a list of nulls and
  // the legend never appears.
  // Only while they can still act on it: a past or future week is not theirs
  // to change, and an empty roster has nothing to captain.
  const needsCaptain =
    canEdit && !captain && [...starters, ...bench].some(Boolean);

  const showSuperDogLegend = anySuperDog(
    [...starters, ...bench].filter(Boolean).map(weeklyLineupUtils.normalizeTeamName),
    gamesBySlug,
  );

  return (
    <div>
      {/* The 5x Play is a one-time-per-season spend, so a manager tapping it
          reasonably wants to know how much room they still have to change
          their mind. It stays undoable right up to the same 15-minute lock
          every other lineup edit gets (migration 049), not the instant it's
          tapped. */}
      {tripPlayTeam && (
        <p className="text-xs mb-4" style={{ color: 'var(--color-text-muted)' }}>
          You can undo this until 15 minutes before kickoff.
        </p>
      )}

      {/* A captain is mandatory, and a lineup saved without one is not refused:
          the 6:30 job sets it instead. So the manager is told that a choice is
          being made for them while they can still make it themselves.

          Deliberately does not spell out which team the job will pick. It
          prefers last week's captain when that team is still in the starters
          and falls back to the biggest favourite, and it skips any team
          already playing, so any short version of that rule is wrong for some
          week. The rules page carries the detail.

          Not phrased as an error. Leaving it to the default is a legitimate
          decision, and this is the same surface that reports refused writes,
          so it is styled apart from them. */}
      {needsCaptain && (
        <div
          className="rounded-lg px-3 py-2.5 mb-4 text-sm flex items-start gap-2"
          style={{ backgroundColor: 'var(--color-captain-fill)', border: '1px solid var(--color-captain-fill)', color: 'var(--color-captain-text)' }}
        >
          <Crown size={16} style={{ color: 'var(--color-captain-deep)', flexShrink: 0, marginTop: 1 }} />
          <span className="flex-1">No Captain Set. Captain Will Be Auto-Assigned.</span>
        </div>
      )}

      {notice && (
        <div
          role="alert"
          className="rounded-lg px-3 py-2.5 mb-4 text-sm flex items-start gap-2"
          style={{ backgroundColor: 'var(--color-danger-bg)', border: '1px solid var(--color-danger-border)', color: 'var(--color-danger-text)' }}
        >
          {/* Capitalized here, not in each message, so the banner cannot end
              up half one style and half the other. It matches the captain
              banner above, which has always read this way. */}
          <span className="flex-1">{titleCase(notice)}</span>
          <button
            type="button"
            onClick={() => setNotice(null)}
            aria-label="Dismiss"
            className="flex-shrink-0 font-semibold"
            style={{ color: 'var(--color-danger-text)' }}
          >
            <X size={16} />
          </button>
        </div>
      )}

      {canEdit && hasChanges && (
        <div className="rounded-lg p-3 mb-4 flex items-center justify-between" style={{ backgroundColor: 'var(--color-success-bg)', border: '1px solid var(--color-success-border)' }}>
          <span className="font-medium text-sm" style={{ color: 'var(--color-text-strong)' }}>You have unsaved changes</span>
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="text-white px-3 py-1.5 rounded-md text-sm font-medium transition-colors disabled:opacity-50"
            style={{ backgroundColor: 'var(--color-success)' }}
          >
            {isSaving ? 'Saving…' : 'Save'}
          </button>
        </div>
      )}

      <>
        {!gamesLoaded ? (
          /* Rows are not rendered at all until the week's fixtures are in
             hand: a row with no game reads as a bye, which is a real state
             and must not be shown to a team that simply has not loaded. */
          <div className="flex items-center justify-center py-16">
            <div style={{ width: 28, height: 28, borderRadius: '50%', border: '2.5px solid var(--color-brand)', borderTopColor: 'transparent', animation: 'spin 0.8s linear infinite' }} />
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          </div>
        ) : (
        <>
        {/* Captain, on its own. It is mandatory every week, it doubles losses
            as well as wins, and the 5x Play can only sit on it, so it is a
            different decision from the other four and reads as one. Still a
            starter: it keeps its slot in `starters` and the section below
            renders the rest. */}
        <div className="mb-5" data-tour="lineup-captain">
          <div className="flex items-center justify-between mb-2 px-1">
            <h4 className="text-xs font-semibold uppercase tracking-wide flex items-center gap-1" style={{ color: 'var(--color-text-muted)' }}>
              <Crown size={12} style={{ color: 'var(--color-captain-deep)' }} />
              Captain
              <HelpMark step="lineup-captain" label="How the captain works" />
            </h4>
            <div className="flex items-center gap-3">
              {/* The 5x lives here now, because the captain's row is the only
                  row that carries it. */}
              <span className="flex items-center gap-1 text-xs" style={{ color: 'var(--color-text-muted)' }}>
                <Zap size={12} style={{ color: 'var(--color-tripplay)' }} />
                5x
              </span>
              <span className="text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--color-text-muted)' }}>Score</span>
            </div>
          </div>
          <div className="rounded-2xl border border-hairline shadow-sm overflow-hidden" style={{ borderColor: 'var(--color-border)' }}>
            {captainIndex >= 0 ? (
              <TeamSlot
                key={`starter-${captainIndex}`}
                team={starters[captainIndex]}
                section="starters"
                index={captainIndex}
                justMoved={justMovedSlots.includes(`starters-${captainIndex}`)}
                week={week}
                leagueId={leagueId}
                canEdit={canEdit}
                isSaving={isSaving}
                captain={captain}
                tripPlayTeam={tripPlayTeam}
                frozenTeams={lineup?.frozenTeams || []}
                frozenPoints={lineup?.teamPoints || {}}
                freezeDetailBySlug={freezeDetailBySlug}
                freezesRemaining={freezesRemaining}
                TeamLogo={TeamLogo}
                onOpenTeam={onOpenTeam}
                onTripPlayToggle={(t) => handleTripPlaySelect(t.school || t.name)}
                onSwapRequest={setSwapFrom}
                onFreezeRequest={handleFreezeRequest}
                readOnly={readOnly}
                onCutRequest={handleTeamCut}
                gamesBySlug={gamesBySlug}
                weeklyPointsBySlug={weeklyPointsBySlug}
                ownerBySlug={ownerBySlug}
                allTeams={allTeams}
              />
            ) : (
              /* No captain yet. Not an error: the 6:30 job sets one, and the
                 banner above says so. This says where it will land. */
              <div className="px-3 py-4 flex items-center gap-2">
                <Crown size={16} style={{ color: 'var(--color-text-faint)', flexShrink: 0 }} />
                <span className="text-sm flex-1" style={{ color: 'var(--color-text-muted)' }}>No captain yet.</span>
                {canEdit && (
                  <button
                    type="button"
                    onClick={() => setSwapFrom({ section: 'captain-pick' })}
                    style={{ minHeight: 32, padding: '0 12px', borderRadius: 999, border: '1.5px solid var(--color-captain-border)', backgroundColor: 'var(--color-captain-fill)', color: 'var(--color-captain-deep)', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}
                  >
                    Choose captain
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="mb-5" style={{ position: 'relative' }}>
          {/* The tour lights up the heading and the first two starters, not
              all four: the whole card is taller than a phone has room for
              beside it. See TourAnchor. */}
          <TourAnchor name="lineup-starters" height={160} />
          <div className="flex items-center justify-between mb-2 px-1">
            <h4 className="text-xs font-semibold uppercase tracking-wide flex items-center gap-1" style={{ color: 'var(--color-text-muted)' }}>
              Starters
              <HelpMark step="lineup-starters" label="How starters score" />
            </h4>
            {/* No captain legend here: the captain has its own labelled
                section above, so a crown key beside the starters named
                nothing on these rows. */}
            <div className="flex items-center gap-3">
              {/* Only once something is frozen. A frozen row keeps the score
                  it had while its game runs on, so it ends up reading a
                  result its points do not match, and the snowflake on the
                  crest is the only thing saying why. Naming it costs a line
                  and stops that reading as a fault. Before the first freeze
                  it would be a legend for a mark nothing on the page carries,
                  and the row is tight at 390px. */}
              {(lineup?.frozenTeams || []).length > 0 && (
                <span className="flex items-center gap-1 text-xs" style={{ color: 'var(--color-text-muted)' }}>
                  <Snowflake size={12} style={{ color: 'var(--color-freeze)' }} />
                  Frozen
                </span>
              )}
              <span className="text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--color-text-muted)' }}>Score</span>
            </div>
          </div>
          <div className="rounded-2xl border border-hairline shadow-sm overflow-hidden" style={{ borderColor: 'var(--color-border)' }}>
            {/* The captain is drawn in its own card above, so it is skipped
                here, but its slot index is still what identifies the row to
                every handler below. Striping cannot use that index: skipping
                one leaves a hole in the sequence, so the rows either side of
                the captain shared a parity and drew the same colour twice in a
                row. rowIndex counts what is actually rendered; index stays the
                slot. */}
            {starters
              .reduce((rows, team, index) => {
                if (index === captainIndex) return rows;
                rows.push({ team, index, rowIndex: rows.length });
                return rows;
              }, [])
              .map(({ team, index, rowIndex }) => (
              <TeamSlot
                key={`starter-${index}`}
                team={team}
                section="starters"
                index={index}
                rowIndex={rowIndex}
                justMoved={justMovedSlots.includes(`starters-${index}`)}
                week={week}
                leagueId={leagueId}
                canEdit={canEdit}
                isSaving={isSaving}
                captain={captain}
                tripPlayTeam={tripPlayTeam}
                frozenTeams={lineup?.frozenTeams || []}
                frozenPoints={lineup?.teamPoints || {}}
                freezeDetailBySlug={freezeDetailBySlug}
                freezesRemaining={freezesRemaining}
                TeamLogo={TeamLogo}
                onOpenTeam={onOpenTeam}
                onTripPlayToggle={(t) => handleTripPlaySelect(t.school || t.name)}
                onSwapRequest={setSwapFrom}
                onFreezeRequest={handleFreezeRequest}
                readOnly={readOnly}
                onCutRequest={handleTeamCut}
                gamesBySlug={gamesBySlug}
                weeklyPointsBySlug={weeklyPointsBySlug}
                ownerBySlug={ownerBySlug}
                allTeams={allTeams}
              />
            ))}
          </div>
        </div>

        <div className="mb-5" data-tour="lineup-bench">
          <div className="flex items-center justify-between mb-2 px-1">
            <h4 className="text-xs font-semibold uppercase tracking-wide flex items-center gap-1" style={{ color: 'var(--color-text-muted)' }}>
              Bench
              <HelpMark step="lineup-bench" label="How the bench works" />
            </h4>
            <span className="text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--color-text-muted)' }}>Score</span>
          </div>
          <div className="rounded-2xl border border-hairline shadow-sm overflow-hidden" style={{ borderColor: 'var(--color-border)' }}>
            {bench.map((team, index) => (
              <TeamSlot
                key={`bench-${index}`}
                team={team}
                section="bench"
                index={index}
                justMoved={justMovedSlots.includes(`bench-${index}`)}
                week={week}
                leagueId={leagueId}
                canEdit={canEdit}
                isSaving={isSaving}
                captain={captain}
                tripPlayTeam={tripPlayTeam}
                frozenTeams={lineup?.frozenTeams || []}
                frozenPoints={lineup?.teamPoints || {}}
                freezeDetailBySlug={freezeDetailBySlug}
                freezesRemaining={freezesRemaining}
                TeamLogo={TeamLogo}
                onOpenTeam={onOpenTeam}
                onTripPlayToggle={(t) => handleTripPlaySelect(t.school || t.name)}
                onSwapRequest={setSwapFrom}
                onFreezeRequest={handleFreezeRequest}
                readOnly={readOnly}
                onCutRequest={handleTeamCut}
                gamesBySlug={gamesBySlug}
                weeklyPointsBySlug={weeklyPointsBySlug}
                ownerBySlug={ownerBySlug}
                allTeams={allTeams}
              />
            ))}
          </div>
        </div>

        </>
        )}

        {showSuperDogLegend && <SuperDogLegend className="mt-3" />}

      </>

      {/* Swap sheet. Every slot can trade with every other slot, so the list
          is the rest of the lineup, grouped the way the page is. Tapping one
          performs the swap and closes: no confirm step, since the move is
          undoable until the team locks. */}
      <BottomSheet
        open={!!swapFrom}
        onClose={() => setSwapFrom(null)}
        title={swapFrom ? (swapFrom.section === 'captain-pick' ? 'Choose captain' : `Swap ${swapLabel(swapFrom, captainIndex)}`) : ''}
      >
        {swapFrom && SWAP_GROUPS.map((group) => {
          const options = group.slots({ starters, bench, captainIndex })
            .filter((slot) => !(slot.section === swapFrom.section && slot.index === swapFrom.index));
          if (options.length === 0) return null;
          return (
            <div key={group.key} style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--color-text-muted)', textTransform: 'uppercase', marginBottom: 6 }}>
                {group.label}
              </div>
              {options.map((slot) => {
                const team = (slot.section === 'starters' ? starters : bench)[slot.index];
                const isCaptainSlot = slot.section === 'starters' && slot.index === captainIndex;
                return (
                  <SwapOptionRow
                    key={`${slot.section}-${slot.index}`}
                    team={team}
                    kind={isCaptainSlot ? 'captain' : slot.section}
                    week={week}
                    captain={captain}
                    tripPlayTeam={tripPlayTeam}
                    frozenTeams={lineup?.frozenTeams || []}
                    frozenPoints={lineup?.teamPoints || {}}
                freezeDetailBySlug={freezeDetailBySlug}
                    gamesBySlug={gamesBySlug}
                    weeklyPointsBySlug={weeklyPointsBySlug}
                    allTeams={allTeams}
                    TeamLogo={TeamLogo}
                    disabled={isSaving || (swapFrom.section === 'captain-pick' && !team)}
                    onSelect={() => (swapFrom.section === 'captain-pick' ? handleCaptainPick(slot) : handleSwap(swapFrom, slot))}
                  />
                );
              })}
            </div>
          );
        })}
      </BottomSheet>

      <ConfirmCutModal
        showConfirmCut={showConfirmCut}
        setShowConfirmCut={setShowConfirmCut}
        confirmCut={confirmCut}
        isSaving={isSaving}
        captain={captain}
        tripPlayTeam={tripPlayTeam}
      />

      <CenteredModal
        open={!!showFreezeConfirm}
        onClose={() => setShowFreezeConfirm(null)}
        icon={Snowflake}
        iconTone="brand"
        title={showFreezeConfirm ? `Freeze ${showFreezeConfirm.team.school}?` : ''}
        primaryLabel="Freeze"
        primaryLoading={isSaving}
        primaryLoadingLabel="Freezing…"
        onPrimary={confirmFreeze}
        secondaryLabel="Cancel"
        onSecondary={() => setShowFreezeConfirm(null)}
      >
        {showFreezeConfirm && (
          <>Locks in {showFreezeConfirm.team.school}'s score at {showFreezeConfirm.points} pts for this week. This cannot be undone. {freezesRemaining} freeze{freezesRemaining === 1 ? '' : 's'} remaining this season.</>
        )}
      </CenteredModal>
    </div>
  );
};

export default WeeklyLineupManager;
